import { Component, signal, inject, OnInit } from '@angular/core';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { Location, ViewportScroller } from '@angular/common';
import { HeaderComponent } from './Shared/header/header';
import { FooterComponent } from './Shared/footer/footer';
import { GoToTopComponent } from './Components/Home/go-to-top/go-to-top';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, FooterComponent, GoToTopComponent],
  templateUrl: './app.html',
  styleUrls: ['./app.css'],
})
export class App implements OnInit {
  protected readonly title = signal('salemobilizerwebsite');

  /** The ordering, rider and partner portals render their OWN layout, so the marketing chrome is hidden there. */
  protected readonly isStorefront = signal(false);

  private router = inject(Router);
  private scroller = inject(ViewportScroller);
  private location = inject(Location);

  /** True until the router's first NavigationEnd fires — distinguishes a real page load/refresh from an in-app link click. */
  private isInitialNavigation = true;

  /** Routes that own their full-page layout (no marketing header/footer). */
  private ownsLayout(url: string): boolean {
    return url.startsWith('/ordering') || url.startsWith('/rider') || url.startsWith('/partner');
  }

  ngOnInit() {
    // Read the real browser URL directly instead of `router.url` — at this point in the
    // lifecycle the Router hasn't finished resolving the initial navigation yet (it's still
    // `/` or mid-redirect), so `router.url` would give the wrong answer for one tick and the
    // marketing header/footer would flash before this corrects itself on NavigationEnd.
    const initialUrl = typeof window !== 'undefined' ? window.location.pathname : this.router.url;
    this.isStorefront.set(this.ownsLayout(initialUrl));
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isStorefront.set(this.ownsLayout(event.urlAfterRedirects));
        const tree = this.router.parseUrl(this.router.url);
        const wasInitialNavigation = this.isInitialNavigation;
        this.isInitialNavigation = false;

        if (!tree.fragment) {
          return;
        }

        if (wasInitialNavigation) {
          // A hard refresh/direct load with a #fragment in the URL should land on the
          // top of the home page, not jump straight to the anchored section.
          this.location.replaceState(this.router.url.split('#')[0]);
          return;
        }

        // Prevent Angular's ViewportScroller from fighting our own scroll.
        this.scroller.setOffset([0, 0]);

        // Wait for the DOM to render the target section, then scroll to it.
        setTimeout(() => {
          this.scrollToFragment(tree.fragment!);
          // Drop the fragment from the URL so a later refresh doesn't re-anchor here.
          this.location.replaceState(this.router.url.split('#')[0]);
        }, 100);
      }
    });
  }

  private scrollToFragment(fragment: string) {
    document.getElementById(fragment)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}